import { useState } from "react";
import { ActivityIndicator } from "react-native";
import { Redirect, useRouter } from "expo-router";

import { AuthScreen } from "~/components/composed/AuthScreen";
import {
  AppText,
  Button,
  ButtonText,
  CenteredScreen,
  Stack,
  TextField,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { useListActions } from "~/state/lists";
import { fetchLists } from "~/state/sync";
import {
  TODAY_PATH,
  isInitialOnboardingComplete,
  withOnboardingComplete,
} from "~/utils/onboarding";

export default function OnboardingScreen() {
  const router = useRouter();
  const {
    session,
    user,
    loading,
    updatePreferences,
    updatePreferencesPending,
    refresh,
  } = useAuth();
  const listActions = useListActions();

  const [name, setName] = useState("Inbox");
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return (
      <CenteredScreen>
        <ActivityIndicator />
      </CenteredScreen>
    );
  }

  // Onboarding only stores a per-account "done" flag, so it's for signed-in
  // users. A guest goes straight to their (local) tasks.
  if (!session?.user) {
    return <Redirect href={TODAY_PATH as never} />;
  }

  if (isInitialOnboardingComplete(user)) {
    return <Redirect href={TODAY_PATH as never} />;
  }

  const finish = async (rename: boolean) => {
    setError(null);
    try {
      const trimmed = name.trim();
      if (rename && trimmed && session?.user.id) {
        // The starter list is provisioned server-side right at sign-up, so
        // the cached lists from useLists() can still be empty here -- fetch
        // it fresh rather than risk silently skipping the rename.
        const [starterList] = await fetchLists(session.user.id);
        if (starterList) {
          await listActions.updateAsync(starterList.id, { name: trimmed });
        }
      }
      await updatePreferences(withOnboardingComplete(user?.preferences));
      await refresh();
      router.replace(TODAY_PATH as never);
    } catch (finishError) {
      setError(
        finishError instanceof Error
          ? finishError.message
          : "Could not finish setting up.",
      );
    }
  };

  const busy = updatePreferencesPending;

  return (
    <AuthScreen
      title="Welcome to TaskScribe"
      subtitle="Name your first list, then start writing down the things that come around again."
    >
      <Stack gap="md">
        {error ? (
          <AppText tone="danger" size="xs">
            {error}
          </AppText>
        ) : null}

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            What should we call your first list?
          </AppText>
          <TextField
            accessibilityLabel="First list name"
            value={name}
            onChangeText={setName}
            placeholder="Inbox, Household, Work…"
          />
          <AppText size="xs">
            Free accounts keep one list and ten tasks. Pro removes the cap and
            adds alerts and analytics.
          </AppText>
        </Stack>

        <Button disabled={busy} onPress={() => void finish(true)}>
          <ButtonText>{busy ? "Saving..." : "Start writing"}</ButtonText>
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onPress={() => void finish(false)}
        >
          <ButtonText>Skip for now</ButtonText>
        </Button>
      </Stack>
    </AuthScreen>
  );
}
